import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import MainBooker from '~/components/BookingFlow/Information/MainBooker';
import utilStyles from '~/styles/utils.module.css';
import informationStyles from '~/styles/bookingFlow/information.module.css';
import useTranslation from '~/hooks/useTranslation';
import Address from '~/components/BookingFlow/Information/Address';
import { useOptions } from '~/hooks/useOptions';
import { InferredBookingFlowOptions } from '~/types/options';
import Participants from '~/components/BookingFlow/Information/Participants';
import RoomAssignments from '~/components/BookingFlow/Information/RoomAssignments';
import { BookingFlowInformationContext } from '~/context/BookingFlowInformationProvider';
import Checkbox from '~/components/Form/Checkbox';
import FormGroup from '~/components/Form/FormGroup';
import { useMutatePackageInformation } from '~/hooks/bookingFlow/useInformationForm';
import Alert from '~/components/General/Alert';
import Button from '~/components/General/Button';
import BikeRiderHeight from '~/components/BookingFlow/Information/BikeRiderHeight';
import bookingFlowRoutes from '~/routes/bookingFlow';
import { PackageInformationRequest } from '~/types/api';
import Spinner from '~/components/General/Spinner';
import PromoCode from '~/components/BookingFlow/Information/PromoCode';
import scrollToTop from '~/helpers/scrollTo';
import IconArrowRight from '~icons/tabler/arrow-right';
import IconArrowLeft from '~icons/tabler/arrow-left';
import BookingFlowLayout from '../Layout/Layout';

function BookingFlowInformation() {
  const translate = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const options = useOptions() as InferredBookingFlowOptions;
  const formContext = useContext(BookingFlowInformationContext);
  const packageInformationMutation = useMutatePackageInformation();

  const {
    informationFormValue: formData,
    packageSessionData,
    packageSessionError,
    packageSessionPending,
  } = formContext;

  const form = useForm<PackageInformationRequest>({
    defaultValues: formData,
    mode: 'onChange',
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData, form]);

  const onFormSubmit = useCallback(async () => {
    // This should never happen, but for types sake we need to check if the form data is set.
    if (!formData) {
      throw new Error('Form data is not set');
    }

    const body = {
      ...form.getValues(),
      locale: options.locale,
    };

    setIsLoading(true);

    try {
      // Mutate the package information to the backend.
      await packageInformationMutation.mutateAsync(body);

      scrollToTop();

      // If the mutation is successful, go to the next step.
      navigate(bookingFlowRoutes.SUMMARY);
    } catch (error) {
      // Errors, validation errors are handled by the mutation object.
      // So we don't have to handle them here.
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    navigate,
  ]);

  const submitButton = useMemo(() => {
    return (
      <div className={informationStyles.buttons}>
        <Button
          type="router-link"
          href={bookingFlowRoutes.OVERVIEW}
          disabled={isLoading}
          styleType="outlineSecondary"
        >
          <IconArrowLeft />
          <span>{translate('bookingFlow.back')}</span>
        </Button>

        <Button
          onClick={form.handleSubmit(onFormSubmit)}
          disabled={isLoading}
          loading={isLoading}
          loadingText={translate('bookingFlow.saving')}
          className={informationStyles.submitButton}
        >
          {translate('bookingFlow.continue_to_summary')}
          <IconArrowRight />
        </Button>
      </div>
    );
  }, [
    translate,
    onFormSubmit,
    isLoading,
    form,
  ]);

  if (packageSessionPending) {
    return <Spinner />;
  }

  if (!formData || !packageSessionData || packageSessionError) {
    return null;
  }

  return (
    <BookingFlowLayout
      step={1}
      sidebarVisible
      showDisclaimer
      sidebarActions={submitButton}
      contentActions={submitButton}
      summary={packageSessionData?.summary}
    >
      <h2 className={utilStyles.srOnly}>
        {translate('bookingFlow.information.title')}
      </h2>

      <Alert
        type="danger"
        focusOnRender
        error={packageInformationMutation.error}
      />

      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...form}>
        <MainBooker
          className={utilStyles.mbMedium}
          registerFieldPath="main_booker"
          participantsFieldPath="participants.0"
        />

        <Address
          className={utilStyles.mbMedium}
          registerFieldPath="address"
        />

        <Participants
          className={utilStyles.mbMedium}
          registerFieldPath="participants"
        />

        <RoomAssignments
          className={utilStyles.mbMedium}
          selectedRoomTypes={packageSessionData?.room_types_selected ?? []}
          registerFieldPath="room_assignments"
        />

        <BikeRiderHeight
          className={utilStyles.mbMedium}
          registerFieldPath="bike_rider_height"
          orderLines={packageSessionData?.order_lines ?? []}
        />

        <PromoCode
          registerField="promo_code"
        />

        <FormGroup>
          <Checkbox
            label={translate('bookingFlow.information.agreement', {
              agreementUrl: options.termsOfServiceUrl,
              privacyUrl: options.privacyPolicyUrl,
            })}
            registerField="agreement"
            required
          />
        </FormGroup>

        <FormGroup>
          <Checkbox
            label={translate('bookingFlow.information.newsletter')}
            registerField="newsletter"
          />
        </FormGroup>
      </FormProvider>
    </BookingFlowLayout>
  );
}

export default BookingFlowInformation;
